import React, { useCallback, useMemo } from 'react';

import { EOrderAsLastTimeState } from '@model';
import { useTrackCheckoutCloseRepeatedPurchase, EcommerceSectionNamespace } from '@services/ecommerceService';
import { PaymentError } from '@services/paymentService';
import { usePrzelewyOaltApi } from '@services/paymentService';

import { useCartPageContext } from '../../../../context';
import { createEcommerceOaltCheckout } from '../../../../ecommerce';
import { IOaLTProcessProps, TOaLTProcessOutput } from '../../model';
import { generalMessages } from '../generalMessages';
import { useCheckoutInitOrder } from '../useCheckoutInitOrder';

import { przelewyPaypoMessages } from './przelewyPaypoMessages';

export const usePrzelewyPaypoProcessForOalt = ({
  setState,
  lastOrder,
  onClose,
  setLoading,
  pageState,
}: IOaLTProcessProps & {
  onClose: () => void;
  setLoading: (isLoading: boolean) => void;
  pageState: EOrderAsLastTimeState;
}): TOaLTProcessOutput => {
  const {
    state: { pricesSummary, products, serviceItems },
  } = useCartPageContext();

  const trackCloseOalt = useTrackCheckoutCloseRepeatedPurchase();
  const { request: checkoutInitOrder } = useCheckoutInitOrder();

  const trackClose = useCallback(() => {
    if (lastOrder) {
      trackCloseOalt(
        createEcommerceOaltCheckout(lastOrder, products, pricesSummary, serviceItems),
        EOrderAsLastTimeState.ERROR,
      );
    }
  }, [lastOrder, pricesSummary, products, serviceItems, trackCloseOalt]);

  const onRepeatButton = useCallback(() => {
    trackClose();
    setState(EOrderAsLastTimeState.INFO_PAGE);
    setLoading(false);
  }, [trackClose, setState, setLoading]);

  const onCloseButton = useCallback(() => {
    onClose();
    onRepeatButton();
  }, [onClose, onRepeatButton]);

  const { onInit, goToPrzelewy } = usePrzelewyOaltApi();

  return useMemo(() => {
    return {
      actionComponent: lastOrder && pageState === EOrderAsLastTimeState.ERROR && (
        <PaymentError
          messages={{ ...przelewyPaypoMessages, ...generalMessages }}
          onRepeatPayment={onRepeatButton}
          onReturnToPreviousStep={onCloseButton}
          amount={lastOrder.orderPricesSummary?.totalPrice ?? 0}
          provider={EcommerceSectionNamespace.EEcommercePaymentProvider.PRZELEWY24}
          providerType={EcommerceSectionNamespace.EEcommercePaymentProvider.PRZELEWY24}
          titleMessage={przelewyPaypoMessages.paymentErrorMessage}
        />
      ),

      onContinueToCheckout: async () => {
        if (!lastOrder) {
          return;
        }
        setLoading(true);
        const result = await checkoutInitOrder(lastOrder);

        if (!result) {
          setState(EOrderAsLastTimeState.ERROR);
          return;
        }

        const responseInit = await onInit();
        if (!responseInit?.token) {
          setState(EOrderAsLastTimeState.ERROR);
          return;
        }

        goToPrzelewy(responseInit.token);
      },
    };
  }, [
    lastOrder,
    pageState,
    onRepeatButton,
    onCloseButton,
    setLoading,
    checkoutInitOrder,
    onInit,
    goToPrzelewy,
    setState,
  ]);
};
